import { CalendarDays, CreditCard, GitCompareArrows, Globe, Hotel, LayoutGrid, ListFilter, ShieldCheck, Utensils } from "lucide-react";
import FilterBar from "../../../components/controls/FilterBar";
import MultiSelectField from "../../../components/controls/MultiSelectField";
import AvailableDatePicker from "./AvailableDatePicker";
import { CARD_CLASS, FILTER_SELECT_CLASS } from "../styles";

type Option = { label: string; value: string };

export type DateDeltaFiltersValue = {
  dateFrom: string;
  dateTo: string;
  category: string[];
  ota: string[];
  boardType: string[];
  refundPolicy: string[];
  paymentType: string[];
  requestDate: string;
  hotel: string[];
};

type Props = {
  filters: DateDeltaFiltersValue;
  options: {
    category: Option[];
    ota: Option[];
    boardType: Option[];
    refundPolicy: Option[];
    paymentType: Option[];
    hotel: Option[];
  };
  loading?: boolean;
  applyDisabled?: boolean;
  parsingDateAvailability?: {
    dates: string[];
    loading?: boolean;
    error?: string | null;
  };
  onChange: (value: DateDeltaFiltersValue) => void;
  onApply: () => void;
  onReset: () => void;
};

export default function DateDeltaFiltersSection({
  filters,
  options,
  loading = false,
  applyDisabled = false,
  parsingDateAvailability,
  onChange,
  onApply,
  onReset,
}: Props) {
  const datesLoading = Boolean(parsingDateAvailability?.loading);
  const datesError = parsingDateAvailability?.error ?? null;
  const datePickerDisabled = loading || datesLoading || Boolean(datesError);

  return (
    <section className={CARD_CLASS}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <ListFilter size={18} />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Фильтры</p>
            <p className="mt-2 text-sm text-slate-500">Выберите период заселения, дату парсинга и нужные параметры сравнения</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onApply}
            disabled={loading || applyDisabled}
            className="rounded-2xl border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-[0_10px_22px_rgba(99,102,241,0.15)] transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Применить
          </button>
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Сбросить
          </button>
        </div>
      </div>

      <FilterBar
          onApply={undefined}
          onReset={undefined}
          hasActive
          sticky={false}
          className="!mt-4 !border-0 !bg-transparent !p-0 !shadow-none"
      >

        <div className="min-w-[220px] flex-1">
          <MultiSelectField
              value={filters.category}
              onChange={(value) => onChange({...filters, category: value})}
              placeholder="Категория"
              icon={<LayoutGrid size={18}/>}
              options={options.category}
              className={FILTER_SELECT_CLASS}
              disabled={loading}
          />
        </div>

        <div className="min-w-[220px] flex-1">
          <MultiSelectField
              value={filters.ota}
              onChange={(value) => onChange({...filters, ota: value})}
              placeholder="OTA"
              icon={<Globe size={18}/>}
              options={options.ota}
              className={FILTER_SELECT_CLASS}
              disabled={loading}
          />
        </div>

        <div className="min-w-[220px] flex-1">
          <MultiSelectField
              value={filters.boardType}
              onChange={(value) => onChange({...filters, boardType: value})}
              placeholder="Питание"
              icon={<Utensils size={18}/>}
              options={options.boardType}
              className={FILTER_SELECT_CLASS}
              disabled={loading}
          />
        </div>

        <div className="min-w-[260px] flex-1">
          <MultiSelectField
              value={filters.hotel}
              onChange={(value) => onChange({...filters, hotel: value})}
              placeholder="Отели"
              icon={<Hotel size={18}/>}
              options={options.hotel}
              className={FILTER_SELECT_CLASS}
              disabled={loading}
          />
        </div>

        <div className="min-w-[220px] flex-1">
          <MultiSelectField
              value={filters.refundPolicy}
              onChange={(value) => onChange({...filters, refundPolicy: value})}
              placeholder="Условия возврата"
              icon={<ShieldCheck size={18}/>}
              options={options.refundPolicy}
              className={FILTER_SELECT_CLASS}
              disabled={loading}
          />
        </div>

        <div className="min-w-[220px] flex-1">
          <MultiSelectField
              value={filters.paymentType}
              onChange={(value) => onChange({...filters, paymentType: value})}
              placeholder="Тип оплаты"
              icon={<CreditCard size={18}/>}
              options={options.paymentType}
              className={FILTER_SELECT_CLASS}
              disabled={loading}
          />
        </div>

        <div className="min-w-[220px] flex-1">
          <AvailableDatePicker
              value={filters.dateFrom}
              onChange={(value) => onChange({...filters, dateFrom: value, dateTo: filters.dateTo && filters.dateTo < value ? value : filters.dateTo})}
              availableDates={[]}
              disabled={loading}
              label="Дата заселения от"
              icon={<CalendarDays size={18}/>}
              variant="field"
              restrictToAvailableDates={false}
          />
          <p className="mt-2 text-xs text-slate-400">Дата заселения от</p>
        </div>

        <div className="min-w-[220px] flex-1">
          <AvailableDatePicker
              value={filters.dateTo}
              onChange={(value) => onChange({...filters, dateTo: value})}
              availableDates={[]}
              disabled={loading}
              minDate={filters.dateFrom || undefined}
              label="Дата заселения до"
              icon={<CalendarDays size={18}/>}
              variant="field"
              restrictToAvailableDates={false}
          />
          <p className="mt-2 text-xs text-slate-400">Дата заселения до</p>
        </div>

        <div className="min-w-[220px] flex-1">
          <AvailableDatePicker
              value={filters.requestDate}
              onChange={(value) => onChange({...filters, requestDate: value})}
              availableDates={parsingDateAvailability?.dates ?? []}
              loading={datesLoading}
              error={datesError}
              disabled={datePickerDisabled}
              label="Дата парсинга"
              icon={<GitCompareArrows size={18}/>}
              variant="field"
          />
          <p className="mt-2 text-xs text-slate-400">Дата парсинга</p>
          {datesError && <p className="mt-1 text-xs font-medium text-rose-500">{datesError}</p>}
        </div>

      </FilterBar>
    </section>
  );
}
